<template>
  <main class="staff-page">
    <header class="page-heading">
      <div>
        <div class="page-eyebrow">Доступ к серверам</div>
        <h1>Активные соединения</h1>
      </div>
    </header>
    <Card>
      <div class="table-toolbar">
        <div class="segmented-control" role="group" aria-label="Подключения">
          <Button
            size="sm"
            :variant="tab === 0 ? 'secondary' : 'ghost'"
            :aria-pressed="tab === 0"
            @click="tab = 0"
          >
            Все подключения
          </Button>
          <Button
            size="sm"
            :variant="tab === 1 ? 'secondary' : 'ghost'"
            :aria-pressed="tab === 1"
            @click="tab = 1"
          >
            Мои подключения
          </Button>
        </div>
        <label class="ui-field table-search">
          <span class="sr-only">Поиск</span>
          <Input v-model="search" type="search" placeholder="Поиск" />
        </label>
      </div>
      <div v-if="loading" class="ui-empty">
        <Spinner />
        <span>Загрузка подключений…</span>
      </div>
      <template v-else>
        <div class="ui-table-scroll">
          <table class="ui-table connection-table">
            <thead>
              <tr>
                <th
                  v-for="column in connectionColumns"
                  :key="column.key"
                  :class="{ num: column.numeric }"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    class="table-sort"
                    @click="setConnectionSort(column.key)"
                  >
                    {{ column.label }}
                    <ArrowDownUp :size="13" />
                  </Button>
                </th>
                <th><span class="sr-only">Действия</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pagedConnections" :key="item.slotId">
                <td>
                  <router-link
                    :to="{ name: 'property-detail', params: { id: item.propertyId } }"
                    class="ui-link font-medium"
                  >
                    {{ item.property }}
                  </router-link>
                </td>
                <td>{{ item.slotInfo }}</td>
                <td>{{ item.product || '—' }}</td>
                <td>
                  <span class="inline-flex items-center gap-2">
                    <span class="staff-avatar staff-avatar--small">
                      {{ item.userInfo.charAt(0) }}
                    </span>
                    {{ item.userInfo }}
                  </span>
                </td>
                <td class="num whitespace-nowrap">{{ formatDateTime(item.date_started) }}</td>
                <td>
                  <Button
                    v-if="isCanBeReleased(item)"
                    size="sm"
                    variant="ghost"
                    class="text-destructive"
                    :disabled="releaseLoad"
                    @click="release(item.slotId)"
                  >
                    <Spinner v-if="releaseLoad" />
                    Освободить
                  </Button>
                </td>
              </tr>
              <tr v-if="!searchedConnections.length">
                <td colspan="6" class="text-center text-muted-foreground py-8">
                  Нет подключений по выбранным фильтрам
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-pagination">
          <label class="flex items-center gap-2 text-xs text-muted-foreground">
            Строк
            <select
              v-model.number="connectionPageSize"
              class="ui-select"
              aria-label="Строк на странице"
            >
              <option v-for="size in [25, 50, 75, 100, -1]" :key="size" :value="size">
                {{ size === -1 ? 'Все' : size }}
              </option>
            </select>
          </label>
          <span>{{ searchedConnections.length }} подключений</span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Предыдущая страница"
            :disabled="connectionPage === 1"
            @click="connectionPage--"
          >
            <ChevronLeft :size="16" />
          </Button>
          <span>{{ connectionPage }} / {{ connectionPageCount }}</span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Следующая страница"
            :disabled="connectionPage >= connectionPageCount"
            @click="connectionPage++"
          >
            <ChevronRight :size="16" />
          </Button>
        </div>
      </template>
    </Card>
  </main>
</template>

<script setup lang="ts">
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { computed } from 'vue';
import { ArrowDownUp, ChevronLeft, ChevronRight } from '@lucide/vue';

import { ref, watch, onMounted } from 'vue';
import axios from 'axios';
import { useMainStore } from '@/store';
import { dateTimeToDateWithTimeString } from '@/filters/date_filters';

const store = useMainStore();

const loading = ref(true);
const releaseLoad = ref(false);
const search = ref('');
const tab = ref(0);

interface FilteredConnection {
  slotId: number;
  property: string;
  propertyId: number;
  slotInfo: string;
  product: string;
  userInfo: string;
  date_started: any;
}

const filteredConnections = ref<FilteredConnection[]>([]);
const allConnections = ref<Connection[]>([]);

function formatDateTime(dateTime: string): string {
  return dateTimeToDateWithTimeString(dateTime) || '';
}

watch(tab, async () => {
  compileTableConnections();
});

function isCanBeReleased(item: any): boolean {
  const connection = allConnections.value.find((x) => x.slot.id === item.slotId) as Connection;

  if (connection && connection.user.username == store.currentUserName) {
    return true;
  }

  return false;
}

async function release(id: number) {
  releaseLoad.value = true;

  await axios.post('/api/slots/' + id + '/release/').then(
    (response) => {
      if (response.status == 201) {
        loadConnections();
      }
    },
    () => undefined
  );
  releaseLoad.value = false;
}

function compileTableConnections() {
  filteredConnections.value = [];
  allConnections.value.forEach((element) => {
    if (filterConnection(element)) {
      const item: FilteredConnection = {
        slotId: element.slot.id,
        property: element.slot.property.name,
        propertyId: element.slot.property.id,
        slotInfo: `${element.slot.type}: ${element.slot.account ? element.slot.account : ''}
                                    ${element.slot.server_address ? '(' + element.slot.server_address + ')' : ''}`,
        product: element.slot.product ? element.slot.product.name : '',
        userInfo: `${element.user.first_name} ${element.user.last_name} (${element.user.username})`,
        date_started: element.date_started,
      };
      filteredConnections.value.push(item);
    }
  });
}

function filterConnection(connection: Connection): boolean {
  switch (tab.value) {
    case 0:
      break;
    case 1:
      if (connection.user.username != store.currentUserName) return false;
  }
  return true;
}

async function loadConnections() {
  loading.value = true;
  allConnections.value = [];
  try {
    let next: string | null = '/api/connections/';
    while (next != null && next != '') {
      console.log('Fetching:', next);
      const items = (await axios.get(next)).data as PagedList<Connection>;
      console.log('Items received:', items.results.length);
      items.results.forEach((element) => {
        allConnections.value.push(element as Connection);
      });
      next = items.next;
    }
    compileTableConnections();
  } catch (e) {
    console.error('Error loading connections:', e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await loadConnections();
});

const connectionPage = ref(1);
const connectionPageSize = ref(50);
const connectionSort = ref<{ key: keyof FilteredConnection; descending: boolean }>({
  key: 'date_started',
  descending: true,
});
const connectionColumns: { key: keyof FilteredConnection; label: string; numeric?: boolean }[] = [
  { key: 'property', label: 'Объект' },
  { key: 'slotInfo', label: 'Слот' },
  { key: 'product', label: 'Продукт' },
  { key: 'userInfo', label: 'Пользователь' },
  { key: 'date_started', label: 'Дата подключения', numeric: true },
];
const searchedConnections = computed(() => {
  const q = search.value.trim().toLowerCase();
  return filteredConnections.value
    .filter(
      (item) => !q || Object.values(item).some((value) => String(value).toLowerCase().includes(q))
    )
    .sort((a, b) => {
      const order = String(a[connectionSort.value.key] ?? '').localeCompare(
        String(b[connectionSort.value.key] ?? ''),
        'ru',
        { numeric: true }
      );
      return connectionSort.value.descending ? -order : order;
    });
});
const connectionPageCount = computed(() =>
  connectionPageSize.value === -1
    ? 1
    : Math.max(1, Math.ceil(searchedConnections.value.length / connectionPageSize.value))
);
const pagedConnections = computed(() =>
  connectionPageSize.value === -1
    ? searchedConnections.value
    : searchedConnections.value.slice(
        (connectionPage.value - 1) * connectionPageSize.value,
        connectionPage.value * connectionPageSize.value
      )
);
function setConnectionSort(key: keyof FilteredConnection) {
  connectionSort.value = {
    key,
    descending: connectionSort.value.key === key ? !connectionSort.value.descending : false,
  };
}
watch([search, tab, connectionPageSize], () => {
  connectionPage.value = 1;
});
watch(connectionPageCount, (count) => {
  connectionPage.value = Math.min(connectionPage.value, count);
});
</script>
